import { STAFF_FEATURES, type Feature } from './features';
import { SURVEY_STAFF_COPY } from './surveys';
import { REVIEW_COPY } from './teamReview';

export type PortalSection =
  | 'event-forms'
  | 'events'
  | 'waivers'
  | 'tournaments'
  | 'profile'
  | 'league'
  | 'clinics'
  | 'dropin'
  | 'manage-dropins'
  | 'attendance'
  | 'impersonate'
  | 'watch-form'
  | 'change-email'
  | 'team-review';

export const SECTION_TITLES: Record<PortalSection, string> = {
  events: 'Events',
  'event-forms': 'Forms',
  waivers: 'Waivers',
  tournaments: 'Tournaments',
  profile: 'Profile',
  league: 'League',
  clinics: 'Clinics',
  dropin: 'Drop-in',
  'manage-dropins': 'Manage drop-ins',
  attendance: 'Drop-in Attendance',
  impersonate: 'Impersonate',
  'watch-form': SURVEY_STAFF_COPY.action,
  'change-email': 'Change email',
  'team-review': REVIEW_COPY.title,
};

const SECTION_FEATURES: Partial<Record<PortalSection, Feature>> = {
  tournaments: 'tournaments',
  events: 'events',
  'event-forms': 'event_forms',
  'team-review': 'team_review',
  attendance: 'dropin_attendance',
  impersonate: 'super_user',
  'change-email': 'super_user',
  'watch-form': 'super_user',
  'manage-dropins': 'super_user',
};

/** Staff sections stay hidden while impersonating. */
export function visibleSections(
  features: readonly Feature[],
  ownAccount: boolean,
): ReadonlySet<PortalSection> {
  return new Set(
    (Object.keys(SECTION_TITLES) as PortalSection[]).filter((section) => {
      const feature = SECTION_FEATURES[section];
      return (
        feature === undefined ||
        (features.includes(feature) &&
          (ownAccount || !STAFF_FEATURES.includes(feature)))
      );
    }),
  );
}

export function isSection(value: string): value is PortalSection {
  return value in SECTION_TITLES;
}

export function slug(text: string): string {
  return text.trim().toLowerCase().replace(/\s+/g, '-');
}

export function declaredSubsPath(seasonName: string, flavour?: string): string {
  const path = `/league/subs/${slug(seasonName)}`;
  return flavour ? `${path}?flavour=${slug(flavour)}` : path;
}

export function registrationPath(seasonName: string): string {
  return `/league/register/${slug(seasonName)}`;
}

export function parsePath(path: string): {
  section: PortalSection | 'home' | 'not-found';
  teamId: string | null;
  /** The season slug of a declared subs page. */
  subsSeason: string | null;
  /** The season slug of a team registration page. */
  registrationSeason: string | null;
  tournamentId: string | null;
  eventId: string | null;
  waiverDocument: string | null;
} {
  const defaultRouteParams = {
    teamId: null,
    subsSeason: null,
    registrationSeason: null,
    tournamentId: null,
    eventId: null,
    waiverDocument: null,
  };
  const waiver = path.match(/^\/waivers\/([a-z0-9-]+)$/);
  if (waiver)
    return {
      section: 'waivers',
      ...defaultRouteParams,
      waiverDocument: waiver[1]!,
    };
  const eventForm = path.match(/^\/event-forms\/(new|rec[A-Za-z0-9]+)$/);
  if (eventForm)
    return {
      section: 'event-forms',
      ...defaultRouteParams,
      eventId: eventForm[1]!,
    };
  const event = path.match(/^\/events\/(rec[A-Za-z0-9]+)$/);
  if (event)
    return { section: 'events', ...defaultRouteParams, eventId: event[1]! };
  const tournament = path.match(/^\/tournaments\/(rec[A-Za-z0-9]+)\/register$/);
  if (tournament)
    return {
      section: 'tournaments',
      ...defaultRouteParams,
      tournamentId: tournament[1]!,
    };
  if (path === '/' || path === '')
    return { section: 'home', ...defaultRouteParams };
  const subsMatch = path.match(/^\/league\/subs\/([a-z0-9-]+)$/);
  if (subsMatch) {
    return {
      section: 'league',
      ...defaultRouteParams,
      subsSeason: subsMatch[1]!,
    };
  }
  const registrationMatch = path.match(/^\/league\/register\/([a-z0-9-]+)$/);
  if (registrationMatch) {
    return {
      section: 'league',
      ...defaultRouteParams,
      registrationSeason: registrationMatch[1]!,
    };
  }
  const teamMatch = path.match(/^\/league\/([A-Za-z0-9]+)$/);
  if (teamMatch) {
    return { section: 'league', ...defaultRouteParams, teamId: teamMatch[1]! };
  }
  if (path === '/clinics/register/winter27')
    return { section: 'clinics', ...defaultRouteParams };
  const section = path.slice(1);
  return isSection(section)
    ? { section, ...defaultRouteParams }
    : { section: 'not-found', ...defaultRouteParams };
}

export function pageName(path: string): string {
  const { section, teamId, subsSeason, registrationSeason, tournamentId } =
    parsePath(path.split('?')[0]!);
  if (section === 'home') return 'Home Page';
  if (section === 'not-found') return 'Page not found';
  if (subsSeason !== null) return 'Declared Subs Page';
  if (registrationSeason !== null) return 'Team Registration Page';
  if (tournamentId !== null) return 'Tournament Registration Page';
  if (teamId !== null) return 'Team Page';
  return `${SECTION_TITLES[section]} Page`;
}

export function isPortalPath(path: string): boolean {
  return path.startsWith('/') && parsePath(path).section !== 'not-found';
}
